import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp, LogOut, Sparkles, Star, X, Send, Loader2, ChevronDown, MessageCircle,
  CheckCircle2, Mail, Share2, Copy, Check, Link2, BarChart3, ArrowRight,
  ExternalLink, BookOpen, Globe, Swords, Users,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { FeatureGate } from '@/components/FeatureGate';
import AppShell from '@/components/AppShell';

import DashboardScoreRings from '@/components/dashboard/DashboardScoreRings';
import DashboardMonitoring from '@/components/dashboard/DashboardMonitoring';
import DashboardActionPreview from '@/components/dashboard/DashboardActionPreview';
import DashboardActivity from '@/components/dashboard/DashboardActivity';
import DashboardROI from '@/components/dashboard/DashboardROI';
import DashboardBooking from '@/components/dashboard/DashboardBooking';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [wins, setWins] = useState([]);
  const [userTier, setUserTier] = useState('free');
  const [loading, setLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);
  const [leads, setLeads] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [benchmarks, setBenchmarks] = useState(null);
  const [roi, setRoi] = useState(null);
  const [presenceHealth, setPresenceHealth] = useState(null);
  const [bookingUrl, setBookingUrl] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [dashRes, winsRes, meRes] = await Promise.all([
        axios.get(`${API}/dashboard`, { withCredentials: true }),
        axios.get(`${API}/wins`, { withCredentials: true }).catch(() => ({ data: { wins: [] } })),
        axios.get(`${API}/auth/me`, { withCredentials: true }).catch(() => ({ data: { subscription_tier: 'free' } })),
      ]);
      setData(dashRes.data);
      setWins(winsRes.data.wins || []);
      const tier = meRes.data.subscription_tier || 'free';
      setUserTier(tier);

      if (tier === 'professional') {
        const leadsRes = await axios.get(`${API}/leads`, { withCredentials: true }).catch(() => ({ data: { leads: [] } }));
        setLeads(leadsRes.data.leads || []);
      }

      const [benchRes, roiRes, presenceRes, bookingRes] = await Promise.all([
        axios.get(`${API}/benchmarks`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/roi/summary`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/presence-health`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/profile/booking`, { withCredentials: true }).catch(() => ({ data: { booking_url: '' } })),
      ]);
      if (benchRes.data?.has_business) setBenchmarks(benchRes.data);
      if (roiRes.data?.has_business) setRoi(roiRes.data);
      if (presenceRes.data?.has_business) setPresenceHealth(presenceRes.data);
      setBookingUrl(bookingRes.data?.booking_url || '');
    } catch (e) {
      if (e.response?.status === 401) navigate('/');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const pollCheckoutStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 5) return;
    try {
      const res = await axios.get(`${API}/billing/checkout-status/${sessionId}`, { withCredentials: true });
      if (res.data.payment_status === 'paid') {
        setUserTier(res.data.tier || 'starter');
        setUpgradeSuccess(true);
        setTimeout(() => setUpgradeSuccess(false), 5000);
        return;
      }
      setTimeout(() => pollCheckoutStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      setTimeout(() => pollCheckoutStatus(sessionId, attempts + 1), 2000);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const sessionId = searchParams.get('session_id');
    const upgradeParam = searchParams.get('upgrade');
    if (sessionId && upgradeParam === 'success') {
      pollCheckoutStatus(sessionId);
      setSearchParams({});
    }
  }, [fetchAll, searchParams, setSearchParams, pollCheckoutStatus]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FDFBF7' }}>
        <div className="w-8 h-8 rounded-full animate-spin" style={{ border: '2px solid #D4C9B8', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!data?.has_business) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6" style={{ backgroundColor: '#FDFBF7' }}>
        <h2 className="text-[22px] font-semibold mb-3" style={{ fontFamily: "'DM Sans', sans-serif", color: '#3C3226' }}>Welcome to GetChosenFirst</h2>
        <p className="text-center mb-6" style={{ color: '#8C7E6E', fontSize: '15px' }}>Start monitoring to see how AI sees your business.</p>
        <button
          onClick={() => navigate('/onboarding')}
          className="text-[#F0EBE3] px-7 py-3 text-[12px] font-medium uppercase tracking-wider transition-opacity hover:opacity-85"
          style={{ backgroundColor: '#2C2520' }}
          data-testid="start-scan-from-dashboard"
        >
          Begin monitoring
        </button>
        <AppShell active="home" />
      </div>
    );
  }

  const score = data.score || {};
  const actions = data.actions || [];
  const firstName = user?.name?.split(' ')[0] || 'there';

  return (
    <AppShell active="home">
    <div className="min-h-screen pb-24 desktop:pb-8" style={{ backgroundColor: '#FDFBF7' }} data-testid="dashboard-page">
      <header className="px-6 pt-6 pb-2 flex items-center justify-between">
        <div>
          <p className="text-[13px]" style={{ color: '#8C7E6E' }}>Welcome back,</p>
          <h1 className="text-[24px] font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", color: '#3C3226' }}>{firstName}</h1>
          <p className="text-[12px] mt-1" style={{ color: '#8C7E6E' }}>Here's what happened since your last visit.</p>
        </div>
        <button onClick={handleLogout} className="p-2 hover:bg-[#EDE8DF] transition-colors" data-testid="logout-btn">
          <LogOut size={20} strokeWidth={1.5} style={{ color: '#8C7E6E' }} />
        </button>
      </header>

      <main className="px-6 max-w-lg mx-auto tablet:max-w-2xl desktop:max-w-5xl">
        {/* Upgrade success toast */}
        <AnimatePresence>
          {upgradeSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-4 p-4 rounded-sm flex items-center gap-3"
              style={{ backgroundColor: 'rgba(29,158,117,0.06)', border: '1px solid rgba(29,158,117,0.15)' }}
              data-testid="upgrade-success-toast"
            >
              <CheckCircle2 size={18} style={{ color: '#1D6B4F' }} />
              <span className="text-sm font-medium" style={{ color: '#1D6B4F' }}>Welcome to {userTier === 'professional' ? 'Professional' : 'Starter'}! All features unlocked.</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Score Rings + Timeline + Breakdown + Benchmarks */}
        <DashboardScoreRings score={score} presenceHealth={presenceHealth} benchmarks={benchmarks} navigate={navigate} />

        {/* AI Monitoring: Describes You + Accuracy + Insights */}
        <DashboardMonitoring data={data} navigate={navigate} />

        {/* Upgrade prompt */}
        {userTier === 'free' && score.total > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className="mb-4">
            <div className="p-5 rounded-sm text-[#F0EBE3]" style={{ backgroundColor: '#2C2520' }} data-testid="upgrade-prompt">
              <p className="v4-eyebrow mb-2" style={{ color: '#C67A5C' }}>Unlock your potential</p>
              <h3 className="text-lg font-medium mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Your score is {score.total}. Let's improve it.</h3>
              <p className="text-[13px] text-[#F0EBE3]/50 mb-4">Paid plans include weekly content, review requests, and continuous AI monitoring.</p>
              <button
                onClick={() => navigate('/pricing')}
                className="w-full py-3 rounded-none text-[#F0EBE3] text-sm font-medium transition-opacity hover:opacity-85"
                style={{ backgroundColor: '#C67A5C' }}
                data-testid="upgrade-prompt-btn"
              >
                See plans — starts at $39/mo
              </button>
            </div>
          </motion.div>
        )}

        {/* Ask for a Review */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-4">
          <FeatureGate feature="review_solicitation" tier={userTier}>
            <button
              onClick={() => setShowReviewModal(true)}
              className="w-full p-5 rounded-sm text-[#F0EBE3] flex items-center gap-4 transition-opacity hover:opacity-90 text-left group"
              style={{ backgroundColor: '#C67A5C' }}
              data-testid="ask-review-btn"
            >
            <div className="w-12 h-12 rounded-none bg-[#F0EBE3]/15 flex items-center justify-center flex-shrink-0">
              <Star size={22} strokeWidth={1.5} className="text-[#F0EBE3]" />
            </div>
            <div className="flex-1">
              <span className="text-[15px] font-medium block">Ask for a review</span>
              <span className="text-[13px] text-[#F0EBE3]/60">Send a quick text to a happy customer</span>
            </div>
            <ArrowRight size={18} strokeWidth={1.5} className="text-[#F0EBE3]/40 group-hover:text-[#F0EBE3] transition-colors" />
          </button>
          </FeatureGate>
        </motion.div>

        {/* Booking CTA */}
        <DashboardBooking bookingUrl={bookingUrl} navigate={navigate} />

        {/* ROI Tracker */}
        {roi && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.17 }}>
            <DashboardROI roi={roi} />
          </motion.div>
        )}

        {/* Wins + Leads */}
        <DashboardActivity wins={wins} leads={leads} />

        {/* Actions Preview + Action Center CTA */}
        <DashboardActionPreview actions={actions} navigate={navigate} />

        {/* Share Report Card */}
        {data?.score && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }} className="mb-4">
            <button
              onClick={() => setShowShareModal(true)}
              className="w-full p-5 rounded-sm flex items-center gap-4 transition-colors hover:bg-[#EDE8DF] group text-left"
              style={{ border: '1px solid #D4C9B8' }}
              data-testid="share-report-card-btn"
            >
              <div className="w-10 h-10 rounded-none flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(198,122,92,0.08)' }}>
                <Share2 size={18} strokeWidth={1.5} style={{ color: '#C67A5C' }} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-[13px] font-medium block" style={{ color: '#3C3226' }}>Challenge your competitors</span>
                <span className="text-[12px]" style={{ color: '#8C7E6E' }}>Share your score and see how others compare</span>
              </div>
              <ArrowRight size={16} className="flex-shrink-0" style={{ color: '#8C7E6E' }} />
            </button>
          </motion.div>
        )}

        {/* Platform Quick Access */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mb-4" data-testid="platform-quick-access">
          <PlatformQuickAccess navigate={navigate} />
        </motion.div>

        {/* Quick Actions */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.32 }} className="grid grid-cols-2 gap-3 mb-4">
          <button
            onClick={() => navigate('/content')}
            className="p-4 rounded-sm flex flex-col items-center gap-2 transition-colors hover:bg-[#EDE8DF]"
            style={{ border: '1px solid #D4C9B8' }}
            data-testid="go-to-content"
          >
            <Sparkles size={20} strokeWidth={1.5} style={{ color: '#1D6B4F' }} />
            <span className="text-[12px] font-medium" style={{ color: '#3C3226' }}>Content Engine</span>
          </button>
          <button
            onClick={() => navigate('/visibility')}
            className="p-4 rounded-sm flex flex-col items-center gap-2 transition-colors hover:bg-[#EDE8DF]"
            style={{ border: '1px solid #D4C9B8' }}
            data-testid="go-to-visibility"
          >
            <TrendingUp size={20} strokeWidth={1.5} style={{ color: '#C67A5C' }} />
            <span className="text-[12px] font-medium" style={{ color: '#3C3226' }}>Recommendations</span>
          </button>
        </motion.div>
      </main>

      {/* Intel Quick Access */}
      <div className="px-6 max-w-lg mx-auto tablet:max-w-2xl desktop:max-w-5xl">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.34 }} className="grid grid-cols-2 gap-3 mb-4" data-testid="intel-quick-access">
          <button
            onClick={() => navigate('/competitors')}
            className="p-4 rounded-sm flex flex-col items-center gap-2 transition-colors hover:bg-[#EDE8DF]"
            style={{ border: '1px solid #D4C9B8' }}
            data-testid="go-to-competitors"
          >
            <Swords size={20} strokeWidth={1.5} style={{ color: '#3C3226' }} />
            <span className="text-[12px] font-medium" style={{ color: '#3C3226' }}>Competitor Intel</span>
          </button>
          <button
            onClick={() => navigate('/market-intel')}
            className="p-4 rounded-sm flex flex-col items-center gap-2 transition-colors hover:bg-[#EDE8DF]"
            style={{ border: '1px solid #D4C9B8' }}
            data-testid="go-to-market-intel"
          >
            <BarChart3 size={20} strokeWidth={1.5} style={{ color: '#C67A5C' }} />
            <span className="text-[12px] font-medium" style={{ color: '#3C3226' }}>Market Intel</span>
          </button>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.36 }} className="grid grid-cols-1 gap-3 mb-4">
          <button
            onClick={() => navigate('/referrals')}
            className="p-4 rounded-sm flex items-center gap-3 transition-colors hover:bg-[#EDE8DF]"
            style={{ border: '1px solid #D4C9B8' }}
            data-testid="go-to-referrals"
          >
            <div className="w-10 h-10 rounded-none flex items-center justify-center" style={{ backgroundColor: 'rgba(29,107,79,0.06)' }}>
              <Users size={18} strokeWidth={1.5} style={{ color: '#1D6B4F' }} />
            </div>
            <div className="text-left">
              <span className="text-[13px] font-medium block" style={{ color: '#3C3226' }}>Referral Network</span>
              <span className="text-[11px]" style={{ color: '#8C7E6E' }}>Partner with local pros to boost AI mentions</span>
            </div>
          </button>
        </motion.div>
      </div>

      {/* Review Request Modal */}
      <AnimatePresence>
        {showReviewModal && (
          <ReviewRequestModal onClose={() => setShowReviewModal(false)} />
        )}
      </AnimatePresence>

      {/* Share Report Card Modal */}
      <AnimatePresence>
        {showShareModal && (
          <ShareReportModal score={data?.score?.total || 0} onClose={() => setShowShareModal(false)} />
        )}
      </AnimatePresence>
    </div>
    </AppShell>
  );
}


/* Review Request Modal */
function ReviewRequestModal({ onClose }) {
  const [step, setStep] = useState('form');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [template, setTemplate] = useState('default');
  const [templates, setTemplates] = useState([]);
  const [selectedPreview, setSelectedPreview] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reviews/templates`, { withCredentials: true })
      .then(res => {
        setTemplates(res.data.templates || []);
        const def = res.data.templates?.find(t => t.key === 'default');
        if (def) setSelectedPreview(def.preview);
      })
      .catch(() => {});
  }, []);

  const handlePreview = () => {
    if (!customerName.trim() || !customerPhone.trim()) return;
    const tmpl = templates.find(t => t.key === template);
    if (tmpl) {
      setSelectedPreview(tmpl.preview.replace('[Customer]', customerName.trim()));
    }
    setStep('preview');
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/reviews/request`, {
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
        template,
      }, { withCredentials: true });
      setResult(res.data);
      setStep('sent');
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to send review request';
      setResult({ error: detail });
      setStep('sent');
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full max-w-md rounded-sm overflow-hidden"
        style={{ border: '1px solid #D4C9B8', backgroundColor: '#F7F4EF' }}
        onClick={e => e.stopPropagation()}
        data-testid="review-modal"
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-3" style={{ borderBottom: '1px solid #D4C9B8' }}>
          <h2 className="text-lg font-medium" style={{ fontFamily: "'DM Sans', sans-serif", color: '#3C3226' }}>
            {step === 'form' ? 'Ask for a review' : step === 'preview' ? 'Preview message' : 'Sent!'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-none hover:bg-[#EDE8DF] transition-colors" data-testid="close-review-modal">
            <X size={18} style={{ color: '#8C7E6E' }} />
          </button>
        </div>

        <div className="px-6 pb-6 pt-4">
          {step === 'form' && (
            <div className="space-y-4">
              <div>
                <label className="text-[11px] font-medium mb-1.5 block" style={{ color: '#8C7E6E', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Customer name</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                  placeholder="Jane Smith"
                  className="w-full rounded-none px-4 py-3 text-sm outline-none"
                  style={{ border: '1px solid #D4C9B8', color: '#3C3226' }}
                  data-testid="review-customer-name"
                  autoFocus
                />
              </div>

              <div>
                <label className="text-[11px] font-medium mb-1.5 block" style={{ color: '#8C7E6E', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Phone number</label>
                <input
                  type="tel"
                  value={customerPhone}
                  onChange={e => setCustomerPhone(e.target.value)}
                  placeholder="(303) 555-1234"
                  className="w-full rounded-none px-4 py-3 text-sm outline-none"
                  style={{ border: '1px solid #D4C9B8', color: '#3C3226' }}
                  data-testid="review-customer-phone"
                />
              </div>

              <div>
                <button
                  onClick={() => setShowTemplates(!showTemplates)}
                  className="flex items-center gap-2 text-[12px] font-medium transition-colors"
                  style={{ color: '#8C7E6E' }}
                  data-testid="toggle-templates"
                >
                  <MessageCircle size={13} /> Message template
                  <ChevronDown size={13} className={`transition-transform ${showTemplates ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {showTemplates && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-2 mt-2">
                        {templates.map(t => (
                          <button
                            key={t.key}
                            onClick={() => { setTemplate(t.key); setSelectedPreview(t.preview); }}
                            className="w-full text-left p-3 rounded-none text-[12px] transition-colors"
                            style={{
                              backgroundColor: template === t.key ? 'rgba(29,107,79,0.04)' : '#FFFFFF',
                              border: template === t.key ? '1px solid rgba(29,107,79,0.2)' : '1px solid #D4C9B8',
                            }}
                            data-testid={`template-${t.key}`}
                          >
                            <span className="font-medium block mb-1" style={{ color: template === t.key ? '#1D6B4F' : '#3C3226' }}>{t.label}</span>
                            <span className="line-clamp-2" style={{ color: '#8C7E6E' }}>{t.preview}</span>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <button
                onClick={handlePreview}
                disabled={!customerName.trim() || !customerPhone.trim()}
                className="w-full rounded-none text-[#F0EBE3] py-3.5 text-sm font-medium transition-opacity hover:opacity-85 disabled:opacity-40"
                style={{ backgroundColor: '#2C2520' }}
                data-testid="preview-sms-btn"
              >
                Preview message
              </button>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-4">
              <div className="p-4 rounded-none" style={{ border: '1px solid #D4C9B8' }}>
                <p className="text-[11px] font-medium mb-2" style={{ color: '#8C7E6E', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Message to {customerName}</p>
                <p className="text-[13px] leading-relaxed whitespace-pre-wrap" style={{ color: '#3C3226' }} data-testid="sms-preview-text">
                  {selectedPreview}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep('form')}
                  className="flex-1 rounded-none py-3.5 text-sm font-medium transition-colors hover:bg-[#EDE8DF]"
                  style={{ border: '1px solid #D4C9B8', color: '#8C7E6E' }}
                  data-testid="back-to-form-btn"
                >
                  Edit
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending}
                  className="flex-[2] rounded-none text-[#F0EBE3] py-3.5 text-sm font-medium transition-opacity hover:opacity-85 disabled:opacity-60 flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#C67A5C' }}
                  data-testid="send-sms-btn"
                >
                  {sending ? <><Loader2 size={15} className="animate-spin" /> Sending...</> : <><Send size={15} /> Send text</>}
                </button>
              </div>
            </div>
          )}

          {step === 'sent' && (
            <div className="text-center py-4">
              {result?.error ? (
                <>
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(212,68,68,0.06)' }}>
                    <X size={24} style={{ color: '#D44' }} />
                  </div>
                  <p className="text-sm font-medium mb-2" style={{ color: '#D44' }}>Couldn't send</p>
                  <p className="text-[12px]" style={{ color: '#8C7E6E' }}>{result.error}</p>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(29,158,117,0.06)' }}>
                    <Send size={24} style={{ color: '#1D6B4F' }} />
                  </div>
                  <p className="text-sm font-medium mb-1" style={{ color: '#1D6B4F' }}>Review request sent!</p>
                  <p className="text-[12px]" style={{ color: '#8C7E6E' }}>We'll let you know when {customerName} leaves a review.</p>
                </>
              )}
              <button
                onClick={onClose}
                className="mt-6 rounded-none text-[#F0EBE3] px-8 py-3 text-sm font-medium transition-opacity hover:opacity-85"
                style={{ backgroundColor: '#2C2520' }}
                data-testid="done-btn"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}


function ShareReportModal({ score, onClose }) {
  const [reportUrl, setReportUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const createReport = async () => {
      try {
        const res = await axios.post(`${API}/reports/create`, {}, { withCredentials: true });
        setReportUrl(`${window.location.origin}/report/${res.data.report_id}`);
      } catch (e) {
      } finally { setLoading(false); }
    };
    createReport();
  }, []);

  const copyLink = () => {
    navigator.clipboard.writeText(reportUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/20 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        className="rounded-sm w-full max-w-md p-6 relative"
        style={{ border: '1px solid #D4C9B8', backgroundColor: '#F7F4EF' }}
        onClick={e => e.stopPropagation()}
        data-testid="share-report-modal"
      >
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center hover:bg-[#EDE8DF]" style={{ border: '1px solid #D4C9B8' }}>
          <X size={16} style={{ color: '#8C7E6E' }} />
        </button>

        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: 'rgba(198,122,92,0.06)' }}>
            <Share2 size={20} style={{ color: '#C67A5C' }} />
          </div>
          <h3 className="text-xl mb-1" style={{ fontFamily: "'DM Sans', sans-serif", color: '#3C3226' }}>Challenge your competitors</h3>
          <p className="text-[12px]" style={{ color: '#8C7E6E' }}>Share your score card — when they scan, you'll see how you compare.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-4"><Loader2 size={24} className="animate-spin" style={{ color: '#8C7E6E' }} /></div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 p-3 rounded-none" style={{ backgroundColor: '#F7F4EF', border: '1px solid #D4C9B8' }}>
              <Link2 size={14} style={{ color: '#8C7E6E' }} className="flex-shrink-0" />
              <span className="flex-1 text-[12px] truncate" style={{ color: '#8C7E6E' }}>{reportUrl}</span>
              <button onClick={copyLink} className="flex items-center gap-1 text-[12px] font-medium px-3 py-1 rounded-none" style={{ color: '#3C3226', border: '1px solid #D4C9B8', backgroundColor: '#F7F4EF' }} data-testid="copy-report-link">
                {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <a
                href={`sms:?body=I just scored ${score}/100 on AI mentions. See how you compare: ${reportUrl}`}
                className="flex items-center justify-center gap-2 py-3 rounded-none text-[#F0EBE3] text-sm font-medium"
                style={{ backgroundColor: '#2C2520' }}
                data-testid="share-sms"
              >
                <Send size={14} /> Text a competitor
              </a>
              <a
                href={`mailto:?subject=My AI Mention Score&body=I just scored ${score}/100 on AI mentions. See how you compare: ${reportUrl}`}
                className="flex items-center justify-center gap-2 py-3 rounded-none text-sm font-medium"
                style={{ border: '1px solid #D4C9B8', color: '#3C3226' }}
                data-testid="share-email"
              >
                <Mail size={14} /> Email
              </a>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}


/* Platform Quick Access */
const PLATFORMS = [
  { id: 'google_business', name: 'Google Business', url: 'https://business.google.com/', icon: Globe },
  { id: 'yelp', name: 'Yelp', url: 'https://biz.yelp.com/', icon: Star },
  { id: 'facebook', name: 'Facebook', url: 'https://business.facebook.com/', icon: Users },
  { id: 'bing', name: 'Bing Places', url: 'https://www.bingplaces.com/', icon: Globe },
];

const GUIDE_LINKS = [
  { platform: 'facebook', label: 'How to post on Facebook', path: '/guides/facebook' },
  { platform: 'instagram', label: 'How to post on Instagram', path: '/guides/instagram' },
  { platform: 'linkedin', label: 'How to post on LinkedIn', path: '/guides/linkedin' },
  { platform: 'nextdoor', label: 'How to post on Nextdoor', path: '/guides/nextdoor' },
];

function PlatformQuickAccess({ navigate }) {
  return (
    <div className="p-5 rounded-sm bg-[#F7F4EF]" style={{ border: '1px solid #D4C9B8' }} data-testid="quick-access-card">
      <div className="flex items-center gap-2 mb-3">
        <Globe size={16} strokeWidth={1.5} style={{ color: '#1D6B4F' }} />
        <h3 className="text-[13px] font-medium" style={{ color: '#3C3226' }}>Your Platforms</h3>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-3">
        {PLATFORMS.map(p => {
          const Icon = p.icon;
          return (
            <a
              key={p.id}
              href={p.url}
              target="_blank"
              rel="noreferrer"
              className="flex flex-col items-center gap-1 p-2.5 rounded-none transition-colors hover:bg-[#EDE8DF]"
              style={{ backgroundColor: '#F7F4EF' }}
              data-testid={`quick-platform-${p.id}`}
            >
              <Icon size={18} strokeWidth={1.5} style={{ color: '#8C7E6E' }} />
              <span className="text-[10px] font-medium text-center leading-tight" style={{ color: '#8C7E6E' }}>{p.name}</span>
            </a>
          );
        })}
      </div>
      <div className="pt-3" style={{ borderTop: '1px solid #D4C9B8' }}>
        <div className="flex items-center gap-1.5 mb-2">
          <BookOpen size={13} strokeWidth={1.5} style={{ color: '#8C7E6E' }} />
          <span className="text-[11px] font-medium" style={{ color: '#8C7E6E', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Posting Guides</span>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {GUIDE_LINKS.map(g => (
            <button
              key={g.platform}
              onClick={() => navigate(g.path)}
              className="text-left px-3 py-2 rounded-none transition-colors flex items-center gap-1.5 hover:bg-[#EDE8DF]"
              style={{ backgroundColor: '#F7F4EF' }}
              data-testid={`guide-link-${g.platform}`}
            >
              <ExternalLink size={10} style={{ color: '#1D6B4F' }} className="flex-shrink-0" />
              <span className="text-[11px] truncate" style={{ color: '#3C3226' }}>{g.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
